/**
 * Reporting UI helpers — plain-text activityLogs for PDFs and A4 page sizing.
 */

import {
  resolveActorDisplayName,
  StaffMaps,
} from 'pages/Logs/logActor';
import {
  CustomerMaps,
  resolveCustomerDisplayName,
} from 'pages/Logs/logCustomer';
import { toPlainLogMessage } from 'pages/Logs/logFormat';
import { convertDate, formatCombinedSkuDateLabel } from 'utils';

/** A4 height at 96dpi (~297mm). PDF pages use ~65% of that for activity rows. */
export const A4_HEIGHT_PX = Math.round((297 / 25.4) * 96);
/** Height budget for one PDF activity page (~65% of A4). */
export const ACTIVITY_REPORT_PAGE_HEIGHT_PX = Math.round(A4_HEIGHT_PX * 0.65);
/** Row height used to size PDF activity pages (matches compact activity line design). */
export const ACTIVITY_REPORT_ROW_HEIGHT_PX = 40;
/** How many active-activity rows fit on one PDF page (~65% A4). */
export const ACTIVITY_REPORT_MAX_ROWS = Math.max(
  1,
  Math.floor(ACTIVITY_REPORT_PAGE_HEIGHT_PX / ACTIVITY_REPORT_ROW_HEIGHT_PX),
);
/** Preview UI pages — no inner scroll; browse included rows 10 at a time. */
export const ACTIVITY_REPORT_PREVIEW_PER_PAGE = 10;
/** Cover-page description — shared for app alerts. */
export const REPORT_DESCRIPTION_MAX_CHARS = 2000;
/** Mandatory one-line subject on staff reports. */
export const REPORT_SUBJECT_MAX_CHARS = 120;
/** Mandatory when any in-context person is Incident or Alert. */
export const REPORT_ALERT_LEVEL_SUMMARY_MAX_CHARS = 400;

/** Strip activity message markup so reports never carry format characters. */
export const stripActivityFormatCharacters = (raw: string): string => (
  toPlainLogMessage(raw)
);

/** Plain prose summary — allow normal punctuation; flatten newlines; cap length. */
export const filterAlertLevelSummaryInput = (raw: string): string => String(raw || '')
  .replace(/\r\n|\r|\n/g, ' ')
  .slice(0, REPORT_ALERT_LEVEL_SUMMARY_MAX_CHARS);

export const sanitizeAlertLevelSummary = (raw: unknown): string => (
  filterAlertLevelSummaryInput(String(raw || ''))
    .replace(/\s+/g, ' ')
    .trim()
);

/**
 * Resolve `|staffId|` and `&G&customerId&G&` to names, then strip markup.
 * Call this on log messages before freeze / preview so reports never show "by 2".
 */
export const plainReportActivityMessage = (
  raw: string,
  staffMaps: StaffMaps,
  customerMaps: CustomerMaps,
  currency = '',
): string => (
  toPlainLogMessage(raw, {
    resolveActor: (token) => resolveActorDisplayName(String(token), staffMaps),
    resolveCustomer: (token) => resolveCustomerDisplayName(String(token), customerMaps),
    formatPrice: (inner) => (
      currency ? `${currency}${Number(inner).toLocaleString()}` : String(inner)
    ),
  })
);

/** Parse a numeric activity amount — empty / N/A / junk are null (same rules as PDF). */
export const parseReportLogAmount = (value: unknown): number | null => {
  if (value == null || value === '' || value === 'N/A') return null;
  const cleaned = String(value).replace(/[^0-9.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.') return null;
  const amount = Number(cleaned);
  if (!Number.isFinite(amount)) return null;
  return amount;
};

/** Hotel currency + locale thousands — e.g. £1,234. Matches receiver PDF/email. */
export const formatReportLogValue = (value: unknown, currency: string): string | null => {
  const amount = parseReportLogAmount(value);
  if (amount == null) return null;
  return `${currency}${amount.toLocaleString()}`;
};

/** Units column — locale thousands, no currency. */
export const formatReportLogUnits = (units: unknown): string | null => {
  const amount = parseReportLogAmount(units);
  if (amount == null) return null;
  return amount.toLocaleString();
};

/**
 * Display amount for Reference activityLogs / chips — follows /logs value|units header.
 * Non-numeric always shows N/A.
 */
export const formatReportLogAmountDisplay = (
  mode: 'value' | 'units',
  currency: string,
  value: unknown,
  units?: unknown,
): { label: string, amount: number | null, isNa: boolean } => {
  if (mode === 'units') {
    const amount = parseReportLogAmount(units);
    if (amount == null) return { label: 'N/A', amount: null, isNa: true };
    return { label: amount.toLocaleString(), amount, isNa: false };
  }
  const amount = parseReportLogAmount(value);
  const label = formatReportLogValue(value, currency);
  if (label == null || amount == null) return { label: 'N/A', amount: null, isNa: true };
  return { label, amount, isNa: false };
};

const MONTH_INDEX: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

const parseClockToMinutes = (clock: string): { hours: number, minutes: number } | null => {
  const match = clock.trim().match(/^(\d{1,2}):(\d{2})(am|pm)$/i);
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const suffix = match[3].toLowerCase();
  if (suffix === 'pm' && hours < 12) hours += 12;
  if (suffix === 'am' && hours === 12) hours = 0;
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return { hours, minutes };
};

/** Milliseconds for ISO or already-formatted report log datetimes. */
export const parseReportLogDateMs = (raw: unknown): number | null => {
  const s = String(raw || '').trim();
  if (!s) return null;
  const parsed = new Date(s);
  const isIso = !Number.isNaN(parsed.getTime())
    && (s.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(s));
  if (isIso) return parsed.getTime();

  const todayMatch = s.match(/^Today\s+(\d{1,2}:\d{2}(?:am|pm))$/i);
  if (todayMatch) {
    const clock = parseClockToMinutes(todayMatch[1]);
    if (!clock) return null;
    const d = new Date();
    d.setHours(clock.hours, clock.minutes, 0, 0);
    return d.getTime();
  }
  // Midnight times are stripped on freeze — "Today" / "14th Aug" still count.
  if (/^Today$/i.test(s)) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }

  const formatted = s.match(
    /^(\d{1,2})(?:st|nd|rd|th)\s+([A-Za-z]{3})(?:\s+(\d{4}))?\.?\s+(\d{1,2}:\d{2}(?:am|pm))$/i,
  );
  if (formatted) {
    const month = MONTH_INDEX[formatted[2].slice(0, 3).toLowerCase()];
    const clock = parseClockToMinutes(formatted[4]);
    if (month == null || !clock) return null;
    const year = formatted[3] ? Number(formatted[3]) : new Date().getFullYear();
    const d = new Date(year, month, Number(formatted[1]), clock.hours, clock.minutes, 0, 0);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  const dateOnly = s.match(
    /^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3})(?:\s+(\d{4}))?\.?$/i,
  );
  if (dateOnly) {
    const month = MONTH_INDEX[dateOnly[2].slice(0, 3).toLowerCase()];
    if (month == null) return null;
    const year = dateOnly[3] ? Number(dateOnly[3]) : new Date().getFullYear();
    const d = new Date(year, month, Number(dateOnly[1]), 0, 0, 0, 0);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  return null;
};

/** Human-readable log datetime for report previews, alerts, email, and PDF. */
export const formatReportLogDateTime = (raw: unknown): string => {
  const s = String(raw || '').trim();
  if (!s) return '';
  // Combined SKU rows already carry a span or leftover datetime range.
  if (s.includes(' – ') || s.includes(' · ') || /^\d+\s+(year|month|day)/i.test(s)) return s;
  const ms = parseReportLogDateMs(s);
  if (ms == null) return s;
  return convertDate(new Date(ms)).replace('. 00:00am', '').replace('00:00am', '').trim();
};

/** Combined SKU span — "1 day", "3 days", "1 year 2 months". */
export const formatReportLogDateTimeRange = (dates: unknown[]): string => {
  const times = dates
    .map((d) => parseReportLogDateMs(d))
    .filter((n): n is number => n != null);
  if (times.length) {
    return formatCombinedSkuDateLabel(
      new Date(Math.min(...times)),
      new Date(Math.max(...times)),
    );
  }
  return '1 day';
};
